import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Badge } from "react-bootstrap";
import {
    FiClock,
    FiRotateCcw,
    FiTrash2,
    FiUser,
    FiCalendar,
    FiPlus,
    FiAlertTriangle,
    FiDollarSign,
    FiUsers,
    FiInbox
} from "react-icons/fi";
import useAxios from "../../hooks/useAxios";
import swal from "sweetalert";

const PlanVersionsModal = ({
    show,
    onHide,
    planId,
    onRestored,
    canEdit = true,
    onOpenSaveModal
}) => {
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const [{ data: versions, loading: loadingVersions }, getVersions] = useAxios(
        { url: `/plans/${planId}/versions` },
        { manual: true, useCache: false }
    );

    const [{ loading: loadingRestore }, restoreVersion] = useAxios(
        { method: "POST" },
        { manual: true, useCache: false }
    );

    const [{ loading: loadingDelete }, deleteVersion] = useAxios(
        { method: "DELETE" },
        { manual: true, useCache: false }
    );

    useEffect(() => {
        if (show && planId) {
            getVersions();
        }
    }, [show, planId]);

    const handleRestore = (version) => {
        if (!canEdit || loadingRestore) return;

        swal({
            title: `¿Restaurar la Versión #${version.version_number}?`,
            text: `Se sobreescribirán todas las cuentas, dotaciones y valores actuales de la planificación con los guardados en "${version.name}". ¿Deseas continuar?`,
            icon: "warning",
            buttons: ["Cancelar", "Sí, restaurar versión"],
            dangerMode: true
        }).then(async (confirmed) => {
            if (confirmed) {
                setActionLoadingId(`restore-${version.id}`);
                try {
                    const response = await restoreVersion({
                        url: `/plans/${planId}/versions/${version.id}/restore`
                    });

                    swal({
                        title: "¡Planificación restaurada!",
                        text: response?.data?.message || `La versión #${version.version_number} ha sido aplicada exitosamente.`,
                        icon: "success",
                        timer: 2200,
                        buttons: false
                    });

                    onHide?.();
                    onRestored?.();
                } catch (error) {
                    swal({
                        title: "Error al restaurar",
                        text: error?.response?.data?.message || "No se pudo restaurar la versión seleccionada.",
                        icon: "error"
                    });
                } finally {
                    setActionLoadingId(null);
                }
            }
        });
    };

    const handleDelete = (version) => {
        if (loadingDelete) return;

        swal({
            title: `¿Eliminar la Versión #${version.version_number}?`,
            text: `¿Estás seguro de eliminar el respaldo "${version.name}" del historial? Esta acción no se puede deshacer.`,
            icon: "warning",
            buttons: ["Cancelar", "Sí, eliminar"],
            dangerMode: true
        }).then(async (confirmed) => {
            if (confirmed) {
                setActionLoadingId(`delete-${version.id}`);
                try {
                    await deleteVersion({
                        url: `/plans/${planId}/versions/${version.id}`
                    });
                    getVersions();
                } catch (error) {
                    swal({
                        title: "Error",
                        text: error?.response?.data?.message || "No se pudo eliminar la versión.",
                        icon: "error"
                    });
                } finally {
                    setActionLoadingId(null);
                }
            }
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toLocaleString("es-CL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton style={{ borderBottom: "1px solid #e2e8f0", padding: "12px 20px" }}>
                <div className="d-flex align-items-center justify-content-between w-100 me-3">
                    <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>
                        <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: "#eff6ff",
                            color: "#2563eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16
                        }}>
                            <FiClock />
                        </div>
                        <span>Historial de Versiones</span>
                        {versions && versions.length > 0 && (
                            <Badge bg="secondary" pill style={{ fontSize: "0.72rem", fontWeight: 600 }}>
                                {versions.length}
                            </Badge>
                        )}
                    </Modal.Title>

                    {canEdit && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="d-flex align-items-center gap-1"
                            style={{ fontSize: "0.78rem", fontWeight: 600 }}
                            onClick={() => {
                                onHide?.();
                                onOpenSaveModal?.();
                            }}
                        >
                            <FiPlus style={{ fontSize: 13 }} />
                            <span>Nueva versión</span>
                        </Button>
                    )}
                </div>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: "calc(80vh - 120px)", overflowY: "auto", padding: "16px 20px", background: "#f8fafc" }}>
                {loadingVersions ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>Cargando historial de versiones...</p>
                    </div>
                ) : !versions || versions.length === 0 ? (
                    <div className="text-center py-5">
                        <div style={{
                            width: 54,
                            height: 54,
                            borderRadius: "50%",
                            background: "#e2e8f0",
                            color: "#64748b",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 24,
                            marginBottom: 12
                        }}>
                            <FiInbox />
                        </div>
                        <h6 className="fw-bold" style={{ color: "#334155" }}>Aún no hay versiones guardadas</h6>
                        <p className="text-muted mb-3" style={{ fontSize: "0.82rem", maxWidth: 400, margin: "0 auto" }}>
                            Puedes guardar respaldos completos del estado de tu planificación para volver a ellos en cualquier momento.
                        </p>
                        {canEdit && (
                            <Button
                                variant="primary"
                                size="sm"
                                className="d-flex align-items-center gap-1 mx-auto"
                                style={{ fontSize: "0.82rem", fontWeight: 600 }}
                                onClick={() => {
                                    onHide?.();
                                    onOpenSaveModal?.();
                                }}
                            >
                                <FiPlus />
                                <span>Guardar primera versión</span>
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {versions.map((ver) => {
                            const isRestoring = actionLoadingId === `restore-${ver.id}`;
                            const isDeleting = actionLoadingId === `delete-${ver.id}`;

                            return (
                                <div
                                    key={`ver-${ver.id}`}
                                    style={{
                                        background: "#ffffff",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 10,
                                        padding: "14px 16px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                                        transition: "border-color 0.15s, box-shadow 0.15s"
                                    }}
                                >
                                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <span style={{
                                                background: "#2563eb",
                                                color: "#ffffff",
                                                padding: "2px 8px",
                                                borderRadius: 6,
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                                letterSpacing: "0.5px"
                                            }}>
                                                v{ver.version_number}
                                            </span>
                                            <span style={{ fontSize: "0.92rem", fontWeight: 700, color: "#0f172a" }}>
                                                {ver.name}
                                            </span>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            {canEdit && (
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    disabled={isRestoring || isDeleting || actionLoadingId !== null}
                                                    onClick={() => handleRestore(ver)}
                                                    className="d-flex align-items-center gap-1"
                                                    style={{ fontSize: "0.76rem", fontWeight: 600, padding: "4px 10px" }}
                                                    title="Establecer esta versión como el estado actual"
                                                >
                                                    {isRestoring ? (
                                                        <>
                                                            <Spinner size="sm" animation="border" />
                                                            <span>Restaurando...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiRotateCcw style={{ fontSize: 12 }} />
                                                            <span>Restaurar</span>
                                                        </>
                                                    )}
                                                </Button>
                                            )}

                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                disabled={isRestoring || isDeleting || actionLoadingId !== null}
                                                onClick={() => handleDelete(ver)}
                                                style={{ padding: "4px 8px", fontSize: "0.76rem" }}
                                                title="Eliminar esta versión"
                                            >
                                                {isDeleting ? (
                                                    <Spinner size="sm" animation="border" />
                                                ) : (
                                                    <FiTrash2 style={{ fontSize: 12 }} />
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Meta & Notes */}
                                    <div className="d-flex flex-wrap align-items-center gap-3 mb-2" style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                        <span className="d-flex align-items-center gap-1">
                                            <FiCalendar style={{ fontSize: 12 }} />
                                            {formatDate(ver.created_at)}
                                        </span>
                                        {ver.user?.name && (
                                            <span className="d-flex align-items-center gap-1">
                                                <FiUser style={{ fontSize: 12 }} />
                                                {ver.user.name}
                                            </span>
                                        )}
                                    </div>

                                    {ver.description && (
                                        <p style={{
                                            fontSize: "0.8rem",
                                            color: "#334155",
                                            background: "#f1f5f9",
                                            padding: "6px 10px",
                                            borderRadius: 6,
                                            marginBottom: 8
                                        }}>
                                            {ver.description}
                                        </p>
                                    )}

                                    {/* Metrics Chips */}
                                    <div className="d-flex flex-wrap align-items-center gap-2 pt-1 border-top" style={{ borderColor: "#f1f5f9" }}>
                                        <div style={{
                                            background: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: 6,
                                            padding: "2px 8px",
                                            fontSize: "0.74rem",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4
                                        }}>
                                            <FiDollarSign className="text-primary" style={{ fontSize: 11 }} />
                                            <span className="text-muted">Total Gastos:</span>
                                            <strong style={{ color: "#0f172a" }}>
                                                ${Number(ver.total_spent || 0).toLocaleString("es-CL")}
                                            </strong>
                                        </div>

                                        <div style={{
                                            background: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: 6,
                                            padding: "2px 8px",
                                            fontSize: "0.74rem",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4
                                        }}>
                                            <FiUsers className="text-info" style={{ fontSize: 11 }} />
                                            <span className="text-muted">Dotación:</span>
                                            <strong style={{ color: "#0f172a" }}>
                                                {Number(ver.total_staff || 0).toLocaleString("es-CL")} personas
                                            </strong>
                                        </div>

                                        {ver.total_income > 0 && (
                                            <div style={{
                                                background: "#f8fafc",
                                                border: "1px solid #e2e8f0",
                                                borderRadius: 6,
                                                padding: "2px 8px",
                                                fontSize: "0.74rem",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4
                                            }}>
                                                <FiDollarSign className="text-success" style={{ fontSize: 11 }} />
                                                <span className="text-muted">Ingresos:</span>
                                                <strong style={{ color: "#0f172a" }}>
                                                    ${Number(ver.total_income || 0).toLocaleString("es-CL")}
                                                </strong>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer style={{ borderTop: "1px solid #e2e8f0", padding: "10px 20px" }}>
                <Button
                    variant="light"
                    size="sm"
                    onClick={onHide}
                    style={{ fontWeight: 600, fontSize: "0.82rem" }}
                >
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PlanVersionsModal;
